import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ShieldCheck, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useCommerce } from "@/contexts/CommerceContext";


const CartPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const {
    account,
    accountLoading,
    cartIds,
    removeFromCart,
    startFiveM,
    startDiscord,
  } = useCommerce();

  useEffect(() => {
    let active = true;
    api.get("/products")
      .then((response) => {
        if (active) setProducts(response.data.filter((product) => cartIds.includes(product.id)));
      })
      .catch(() => {
        if (active) toast.error("Your basket could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cartIds]);

  const subtotal = useMemo(
    () => products.reduce((total, product) => total + Number(product.price || 0), 0),
    [products],
  );

  const checkout = async () => {
    if (!products.length || checkoutLoading || accountLoading) return;
    setCheckoutLoading(true);
    try {
      if (!account.fivem_connected) {
        toast.info("Connect FiveM first. You will return to your basket afterwards.");
        await startFiveM("/cart");
        return;
      }
      if (!account.discord_connected) {
        toast.info("Connect Discord next. You will return to your basket afterwards.");
        await startDiscord("/cart");
        return;
      }
      const response = await api.post("/checkout/cart", {
        product_ids: products.map((product) => product.id),
      });
      const checkout = response.data;
      if (window.Tebex?.checkout) {
        window.Tebex.checkout.init({
          ident: checkout.ident,
          theme: "dark",
          colors: [
            { name: "primary", color: "#0ea5e9" },
            { name: "secondary", color: "#22d3ee" },
          ],
        });
        window.Tebex.checkout.launch();
        setCheckoutLoading(false);
        return;
      }
      window.location.assign(checkout.checkout_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Secure checkout could not be prepared.");
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-32 md:px-12">
        <div className="mb-10">
          <span className="mb-3 block text-xs uppercase tracking-[0.2em] text-primary">Secure basket</span>
          <h1 className="text-4xl font-bold uppercase tracking-tight md:text-5xl">Your basket</h1>
          <p className="mt-3 text-sm text-slate-400">
            Connect FiveM and Discord, then complete payment securely through Tebex.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-32 rounded-xl bg-slate-900" />
            <div className="h-32 rounded-xl bg-slate-900" />
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-950/50 px-6 py-16 text-center">
            <ShoppingCart className="mx-auto mb-5 h-10 w-10 text-slate-600" />
            <h2 className="text-xl font-bold uppercase tracking-wide">Your basket is empty</h2>
            <p className="mt-2 text-sm text-slate-500">Browse the catalogue and add a script when you are ready.</p>
            <Link to="/products">
              <Button className="mt-7 rounded-lg bg-primary px-7 py-6 font-bold uppercase tracking-wider text-primary-foreground">
                Browse products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="space-y-4">
              {products.map((product) => (
                <article
                  key={product.id}
                  className="grid gap-5 rounded-xl border border-slate-800 bg-slate-950/50 p-4 sm:grid-cols-[180px_minmax(0,1fr)]"
                >
                  <Link to={`/product/${product.id}`} className="block aspect-video overflow-hidden rounded-lg bg-slate-900 sm:aspect-auto">
                    <img
                      src={product.image_url || "/logo-icon.svg"}
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />
                  </Link>
                  <div className="flex min-w-0 flex-col justify-between py-1">
                    <div>
                      <h2 className="text-xl font-bold uppercase tracking-wide">{product.name}</h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-400">{product.short_description}</p>
                    </div>
                    <div className="mt-5 flex items-center justify-between gap-4">
                      <span className="price-tag text-xl">${Number(product.price).toFixed(2)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeFromCart(product.id)}
                        className="text-xs uppercase tracking-wider text-slate-500 hover:bg-red-500/10 hover:text-red-300"
                        aria-label={`Remove ${product.name} from basket`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Remove
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <aside className="h-fit rounded-xl border border-primary/20 bg-slate-950/70 p-6 lg:sticky lg:top-28">
              <h2 className="text-lg font-bold uppercase tracking-wide">Order summary</h2>
              <div className="my-5 space-y-3 border-y border-slate-800 py-5 text-sm">
                <div className="flex justify-between text-slate-400">
                  <span>{products.length} {products.length === 1 ? "product" : "products"}</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-white">
                  <span>Total</span>
                  <span className="text-primary">${subtotal.toFixed(2)} USD</span>
                </div>
              </div>

              <div className="mb-5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">FiveM account</span>
                  <span className={account.fivem_connected ? "text-emerald-400" : "text-amber-300"}>
                    {account.fivem_connected ? "Connected" : "Required"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Discord account</span>
                  <span className={account.discord_connected ? "text-emerald-400" : "text-amber-300"}>
                    {account.discord_connected ? "Connected" : "Required"}
                  </span>
                </div>
              </div>

              <Button
                type="button"
                onClick={checkout}
                disabled={checkoutLoading || accountLoading}
                className="w-full rounded-lg border-l-4 border-accent bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-primary/90"
                data-testid="cart-checkout-button"
              >
                <CreditCard className="mr-2 h-5 w-5" />
                {checkoutLoading
                  ? "Opening…"
                  : !account.fivem_connected
                    ? "Connect FiveM"
                    : !account.discord_connected
                      ? "Connect Discord"
                      : "Checkout with Tebex"}
              </Button>
              <p className="mt-4 flex items-start gap-2 text-xs leading-5 text-slate-500">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                Payment and card details are handled securely by Tebex.
              </p>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
